/**
 * Trusted time.
 *
 * The tablets are guests on St. Lucas's Wi-Fi. NTP (UDP 123) is commonly
 * blocked there, so an Android tablet's own clock is not evidence of anything:
 * a factory-reset device comes up believing it is 2017, and nothing on that
 * network will ever tell it otherwise. The ONLY trustworthy time in this system
 * is the `Date` header on a successful response from our own Worker.
 *
 * So this module never treats `Date.now()` as truth. Three rules hold it up:
 *
 *  1. Corrected time rides a MONOTONIC base (`performance.now()`), anchored to
 *     the last server reading. The device clock can jump years under us — when
 *     Android finally syncs, or when somebody edits the date — and our clock
 *     does not twitch, because it never reads the device clock to tell the
 *     time.
 *
 *  2. The offset is PERSISTED. A tablet rebooted behind a captive portal, or
 *     with the Wi-Fi down entirely, comes back with an approximately correct
 *     clock in its first millisecond rather than ringing 2017's bells while it
 *     waits for a network that may not return today. Persisted time counts as
 *     trusted — it beats a nine-year-wrong device clock by a mile — but
 *     `syncedAt` stays honest so the kiosk can show that it is stale.
 *
 *  3. Corrections are sanity-checked. A single poisoned reading (a captive
 *     portal's own Date header, a stale cached response) must not be able to
 *     shove the clock back across a whole day and replay the morning's bells.
 */

/** Where the offset survives a reboot. */
const STORAGE_KEY = 'valor.clock.v1';

const TIME_URL = '/api/time';

/** A dead captive portal will hold a socket open forever. Do not wait for it. */
const SYNC_TIMEOUT_MS = 8_000;

/**
 * Readings outside this window are not a school clock, they are a fault. The
 * floor is what rejects the factory-reset tablet insisting it is 2017.
 */
const MIN_PLAUSIBLE_MS = Date.UTC(2025, 0, 1);
const MAX_PLAUSIBLE_MS = Date.UTC(2100, 0, 1);

/** Past this much device error, say so in the console. Somebody should know. */
const LOUD_SKEW_MS = 60 * 60 * 1000;

/**
 * A tablet can sit switched off over a summer. It cannot sit switched off for
 * years. Used at boot with no network to decide whether the persisted offset
 * still applies, or whether the device clock got fixed underneath us.
 */
const MAX_OFFLINE_GAP_MS = 400 * 24 * 60 * 60 * 1000;

/**
 * Backwards corrections smaller than this are ordinary drift and round-trip
 * noise. Larger ones must be confirmed by a second, agreeing reading before we
 * act — one bad response should never be able to replay a day of bells.
 */
const BACKWARD_LIMIT_MS = 5 * 60 * 1000;

/** How closely a confirming reading must agree with the held one. */
const CONFIRM_TOLERANCE_MS = 10_000;

/**
 * How often the corrected time is written down while running. A tablet whose
 * RTC stops or resets while it is powered off comes back at whatever we last
 * wrote, so "last wrote" wants to be recent. Costs one ~120-byte write.
 */
const HEARTBEAT_MS = 30_000;

/**
 * An HTTP `Date` header is truncated to the second (RFC 7231), so on average it
 * is half a second behind the instant the server wrote it. `x-server-time` is
 * exact, and is preferred whenever it is present.
 */
const DATE_HEADER_GRANULARITY_MS = 500;

export interface Clock {
  /** Corrected epoch ms. */
  now(): number;
  /** True once synced at least once — including from a persisted offset. */
  readonly trusted: boolean;
  /** The device clock error we are correcting for, right now. */
  readonly skewMs: number;
  /** Corrected epoch ms of the last real network reading. Null if never. */
  readonly syncedAt: number | null;
  adopt(headerDate: string | null, serverTimeMs?: number): void;
  sync(): Promise<boolean>;
}

interface Persisted {
  /** corrected − device, as measured when this was written. */
  skewMs: number;
  /** Corrected epoch ms at the moment of writing. Time never precedes this. */
  correctedAt: number;
  /** Corrected epoch ms of the last real network sync. Honest about staleness. */
  syncedAt: number;
}

/** Monotonic milliseconds. Immune to the device clock being wrong or moved. */
function monoNow(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}

function plausible(ms: number): boolean {
  return Number.isFinite(ms) && ms > MIN_PLAUSIBLE_MS && ms < MAX_PLAUSIBLE_MS;
}

function readPersisted(): Persisted | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Persisted>;
    if (
      typeof parsed?.skewMs !== 'number' ||
      !Number.isFinite(parsed.skewMs) ||
      typeof parsed?.correctedAt !== 'number' ||
      !plausible(parsed.correctedAt)
    ) {
      return null;
    }
    return {
      skewMs: parsed.skewMs,
      correctedAt: parsed.correctedAt,
      syncedAt: typeof parsed.syncedAt === 'number' ? parsed.syncedAt : parsed.correctedAt,
    };
  } catch {
    // Storage disabled or corrupt. We simply have no memory; carry on.
    return null;
  }
}

/**
 * The round trip for the fetch whose headers we are adopting. `adopt()` is
 * called the instant a response resolves, so the most recently finished
 * same-origin request IS that request — Resource Timing already timed it,
 * without the caller having to hand us a stopwatch.
 */
function recentRoundTripMs(): number | null {
  try {
    if (typeof performance?.getEntriesByType !== 'function') return null;
    const now = monoNow();
    let duration: number | null = null;
    let latestEnd = -Infinity;
    for (const raw of performance.getEntriesByType('resource')) {
      const entry = raw as PerformanceResourceTiming;
      const end = entry.responseEnd || entry.startTime + entry.duration;
      if (now - end > 2_000) continue; // Too old to be the request we just made.
      if (end <= latestEnd) continue;
      latestEnd = end;
      duration = entry.duration;
    }
    return duration !== null && duration >= 0 && duration < 60_000 ? duration : null;
  } catch {
    return null;
  }
}

export function createClock(): Clock {
  /** Corrected epoch ms at `anchorMono`. Null until we have any evidence. */
  let anchorEpoch: number | null = null;
  let anchorMono = monoNow();

  /** Corrected epoch of the last genuine network reading. Stale is not absent. */
  let syncedAt: number | null = null;

  /** False while our only evidence is the persisted offset from a past run. */
  let syncedThisSession = false;

  /** Last round trip measured by `sync()`, for when Resource Timing is missing. */
  let lastRttMs: number | null = null;

  /** A large backwards correction, held until a second reading agrees. */
  let pendingBackwards: { epoch: number; mono: number } | null = null;

  function now(): number {
    if (anchorEpoch === null) {
      // Nothing has ever told us the time. The device clock is a bad guess, but
      // it is the only guess; `trusted` is false so the kiosk can say so.
      return Date.now();
    }
    return anchorEpoch + (monoNow() - anchorMono);
  }

  function currentSkew(): number {
    return anchorEpoch === null ? 0 : now() - Date.now();
  }

  function persist(): void {
    if (anchorEpoch === null) return;
    const record: Persisted = {
      skewMs: currentSkew(),
      correctedAt: now(),
      syncedAt: syncedAt ?? now(),
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch {
      // Full or disabled storage costs us the reboot shortcut, nothing more.
    }
  }

  /**
   * Boot from the persisted offset, so a tablet that comes up with no network
   * still knows roughly what time it is.
   *
   * The hard case is deciding which is stale: the device clock, or our memory
   * of how wrong it was. Time only moves forward, so the last corrected time we
   * wrote down is a floor. Carry the remembered skew forward if that lands
   * somewhere plausible (the ordinary blocked-NTP case). If it does not, but
   * the raw device clock does, then Android evidently fixed the clock while we
   * were off and the remembered skew is the stale thing — drop it.
   */
  function restore(): void {
    const saved = readPersisted();
    if (!saved) return;

    const device = Date.now();
    const carried = device + saved.skewMs;
    const floor = saved.correctedAt;
    const fits = (t: number): boolean =>
      plausible(t) && t >= floor - 60_000 && t <= floor + MAX_OFFLINE_GAP_MS;

    let boot: number;
    if (fits(carried)) {
      boot = carried;
    } else if (fits(device)) {
      console.warn(
        `[clock] persisted skew of ${Math.round(saved.skewMs / 1000)}s no longer fits; ` +
          'the device clock appears to have been corrected while we were off',
      );
      boot = device;
    } else {
      // Neither reading is credible. The last time we knew for certain is the
      // safest value available: a bell may be missed, but none is replayed.
      console.warn('[clock] neither the device clock nor the persisted skew is credible at boot');
      boot = Math.min(carried, device);
    }

    // Time does not run backwards. Whatever we just worked out, it cannot be
    // earlier than the last moment we knew for certain — that is what catches
    // a tablet whose RTC stopped or reset while it was powered off.
    if (boot < floor) {
      console.warn(
        `[clock] boot estimate was ${Math.round((floor - boot) / 1000)}s behind the last known ` +
          'good time; clamping forward',
      );
      boot = floor;
    }

    anchorEpoch = boot;
    anchorMono = monoNow();
    syncedAt = saved.syncedAt;

    const staleHours = Math.round(((boot - saved.syncedAt) / 3_600_000) * 10) / 10;
    console.info(
      `[clock] restored from storage: ${new Date(boot).toISOString()} ` +
        `(device says ${new Date(device).toISOString()}; last real sync ${staleHours}h ago)`,
    );
  }

  /** The one place a server reading becomes our clock. */
  function apply(serverEpoch: number, rttMs: number): boolean {
    const mono = monoNow();
    // The server wrote that timestamp roughly half a round trip ago.
    const corrected = serverEpoch + Math.max(0, rttMs) / 2;

    if (!plausible(corrected)) {
      console.error(`[clock] refusing an absurd server time (${serverEpoch}) — reading ignored`);
      return false;
    }

    const before = anchorEpoch === null ? null : anchorEpoch + (mono - anchorMono);
    const delta = before === null ? 0 : corrected - before;

    // A large jump backwards is the one correction that can replay a day of
    // bells, so it has to be confirmed. State restored from storage but never
    // verified this session is not worth protecting — the network wins there.
    const onlyStaleEvidence = before !== null && !syncedThisSession;
    if (before !== null && delta < -BACKWARD_LIMIT_MS && !onlyStaleEvidence) {
      const projected =
        pendingBackwards === null ? null : pendingBackwards.epoch + (mono - pendingBackwards.mono);
      if (projected === null || Math.abs(corrected - projected) > CONFIRM_TOLERANCE_MS) {
        pendingBackwards = { epoch: corrected, mono };
        console.warn(
          `[clock] a reading would move time back ${Math.round(-delta / 1000)}s — ` +
            'holding it until a second reading agrees',
        );
        return false;
      }
      console.warn(`[clock] backwards correction of ${Math.round(-delta / 1000)}s confirmed`);
    }
    pendingBackwards = null;

    anchorEpoch = corrected;
    anchorMono = mono;
    syncedAt = corrected;
    syncedThisSession = true;

    const skew = corrected - Date.now();
    if (Math.abs(skew) > LOUD_SKEW_MS) {
      console.warn(
        `[clock] device clock is off by ${(skew / 3_600_000).toFixed(2)}h — ` +
          `device says ${new Date(Date.now()).toISOString()}, ` +
          `server says ${new Date(corrected).toISOString()}. Correcting.`,
      );
    }

    persist();
    return true;
  }

  /**
   * The main correction path: every successful response from our Worker carries
   * the time, so the schedule poll doubles as a clock sync sixty times an hour.
   */
  function adopt(headerDate: string | null, serverTimeMs?: number): void {
    let serverEpoch: number | null = null;

    if (typeof serverTimeMs === 'number' && plausible(serverTimeMs)) {
      // Millisecond precision, straight from the Worker.
      serverEpoch = serverTimeMs;
    } else if (headerDate) {
      const parsed = Date.parse(headerDate);
      // Whole-second resolution: on average it is half a second behind.
      if (plausible(parsed)) serverEpoch = parsed + DATE_HEADER_GRANULARITY_MS;
    }

    if (serverEpoch === null) {
      console.warn(
        `[clock] unusable server time (date=${headerDate}, x-server-time=${serverTimeMs})`,
      );
      return;
    }

    apply(serverEpoch, recentRoundTripMs() ?? lastRttMs ?? 0);
  }

  /** The standalone path, for when the schedule fetch is not available to ride. */
  async function sync(): Promise<boolean> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), SYNC_TIMEOUT_MS);
    const started = monoNow();
    try {
      const res = await fetch(TIME_URL, {
        method: 'GET',
        cache: 'no-store',
        credentials: 'omit',
        signal: controller.signal,
      });
      const rtt = monoNow() - started;
      lastRttMs = rtt;

      // /api/time answers 204 and nothing else. Anything with a body is a
      // captive portal wearing our URL, and its Date header is not our time.
      if (res.status !== 204) {
        console.warn(`[clock] /api/time answered ${res.status} — not our Worker. Ignoring.`);
        return false;
      }

      const exact = Number(res.headers.get('x-server-time'));
      if (plausible(exact)) return apply(exact, rtt);

      const header = Date.parse(res.headers.get('date') ?? '');
      if (plausible(header)) return apply(header + DATE_HEADER_GRANULARITY_MS, rtt);

      console.warn('[clock] /api/time carried no usable timestamp');
      return false;
    } catch (err) {
      console.warn('[clock] time sync failed', err);
      return false;
    } finally {
      clearTimeout(timer);
    }
  }

  restore();

  // Only in a real browser: under vitest there is no DOM, no storage, and no
  // reason to hold a timer open.
  if (typeof document !== 'undefined') {
    setInterval(persist, HEARTBEAT_MS);
    // The last moment before a tablet is put to sleep or powered off is the
    // most valuable one to have written down.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') persist();
    });
  }

  return {
    now,
    get trusted() {
      return anchorEpoch !== null;
    },
    get skewMs() {
      return currentSkew();
    },
    get syncedAt() {
      return syncedAt;
    },
    adopt,
    sync,
  };
}
